"use client";

import React, { useRef, useEffect, useMemo } from 'react';
import { useSearchParams } from 'next/navigation';
import { UnifiedPromptBar } from '@/app/components/unified-prompt-bar';
import LoadingAnimation from '@/app/components/custom-loading';
import { usePrTheme } from '@/app/components/themeProvider/pr-theme-provider';
import { useAppSelector } from '@/store/hooks';
import { useSelector } from 'react-redux';
import { chatUiSliceSelectors } from '@/store/features/chat/chatUiSlice';
import MessagesWithDateSeparators from '@/app/components/chat-list';
import { toUnifiedMessage } from '@/app/components/chat-core/types/message';
import { useMessageProcessor } from '@/app/components/chat-core/hooks/useMessageProcessor';
import { useIframeManager } from '@/app/components/chat-core/hooks/useIframeManager';
import { SplitScreenLayout } from '@/app/components/chat-core/components/SplitScreenLayout';
import { IframeControls } from '@/app/components/chat-core/components/IframeControls';
import WelcomeMessage from '@/app/components/welcomeMessage';

export interface UnifiedWidgetConfig {
  agent?: string;
  agentFromQueryParam?: boolean;
  defaultAgentId?: string;
  agentNameFromQuery?: boolean;

  enableIframeManagement?: boolean;
  enableDateSeparators?: boolean;

  headerContent?: React.ReactNode;
  mobileHeaderContent?: React.ReactNode;
  welcomeMessage?: React.ReactNode;

  maxWidth?: string;
  showNewChatButton?: boolean;
  showAvatars?: boolean;
  compactMobileSpacing?: boolean;

  usePostMutation: any;
  useVoteMutation?: any;

  onDocumentTitle?: (agentName: string) => void;
  onCustomSubmit?: (message: string, agentId: string, chatId: string, files?: string[]) => Promise<void>; // Custom submit handler (overrides default)
}

export function UnifiedWidgetPage({
  agent,
  agentFromQueryParam = false,
  defaultAgentId,
  agentNameFromQuery = false,
  enableIframeManagement = false,
  headerContent,
  mobileHeaderContent,
  welcomeMessage,
  maxWidth = "min(100%, 48rem)",
  showAvatars = true,
  compactMobileSpacing = false,
  usePostMutation,
  useVoteMutation,
  onDocumentTitle,
  onCustomSubmit,
}: UnifiedWidgetConfig) {
  const params = useSearchParams();
  const theme = usePrTheme();
  const chat = useAppSelector(state => state.chatUI.chat);
  const messages = useSelector(chatUiSliceSelectors.selectMessages);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const messagesContainerRef = useRef<HTMLDivElement>(null);
  const shouldAutoScrollRef = useRef<boolean>(true);
  const isUserScrollingRef = useRef<boolean>(false);
  const hasInitiallyScrolledRef = useRef<boolean>(false);
  const [postNewMessage] = usePostMutation();

  const agentId = agentFromQueryParam
    ? params.get('agentId') ?? defaultAgentId ?? ''
    : agent ?? '';

  const agentName = agentNameFromQuery
    ? params.get('agentName') ?? 'Agent'
    : '';

  useEffect(() => {
    if (onDocumentTitle && agentName) {
      onDocumentTitle(agentName);
    }
  }, [agentName, onDocumentTitle]);

  const unifiedMessages = useMemo(() =>
    enableIframeManagement ? messages.map(toUnifiedMessage) : [],
    [messages, enableIframeManagement]
  );

  const { processedMessages, iframeMessages } = useMessageProcessor(
    unifiedMessages,
    {
      separateIframes: enableIframeManagement,
      autoScroll: false,
    }
  );

  const iframeManager = useIframeManager(enableIframeManagement ? iframeMessages : []);

  const chatMessages = useMemo(() => {
    if (!enableIframeManagement) return messages;

    return messages.map(message => {
      if (message.type === 'ai') {
        const processed = processedMessages.find(p => p.id === message.id);
        if (processed && processed.hasIframe) {
          return {
            ...message,
            message: processed.displayContent,
            hasIframe: true,
            iframeContent: processed.iframeContent,
          };
        }
      }
      return message;
    });
  }, [messages, processedMessages, enableIframeManagement]);

  const isNearBottom = (container: HTMLDivElement, threshold: number = 150): boolean => {
    const { scrollTop, scrollHeight, clientHeight } = container;
    const distanceFromBottom = scrollHeight - scrollTop - clientHeight;
    return distanceFromBottom <= threshold;
  };

  useEffect(() => {
    const container = messagesContainerRef.current;
    if (!container) return;

    if (isNearBottom(container)) {
      shouldAutoScrollRef.current = true;
    }

    let scrollTimeout: NodeJS.Timeout;

    const handleScroll = () => {
      isUserScrollingRef.current = true;

      clearTimeout(scrollTimeout);

      if (isNearBottom(container)) {
        shouldAutoScrollRef.current = true;
      } else {
        shouldAutoScrollRef.current = false;
      }

      scrollTimeout = setTimeout(() => {
        isUserScrollingRef.current = false;
      }, 150);
    };

    container.addEventListener('scroll', handleScroll, { passive: true });

    return () => {
      container.removeEventListener('scroll', handleScroll);
      clearTimeout(scrollTimeout);
    };
  }, []);

  useEffect(() => {
    if (messages.length > 0 && messagesContainerRef.current) {
      const container = messagesContainerRef.current;
      if (isNearBottom(container)) {
        shouldAutoScrollRef.current = true;
      }
    }
  }, [messages.length]);

  useEffect(() => {
    if (messages.length > 0 && messagesContainerRef.current) {
      requestAnimationFrame(() => {
        setTimeout(() => {
          const container = messagesContainerRef.current;
          if (container) {
            if (!hasInitiallyScrolledRef.current) {
              container.scrollTop = container.scrollHeight;
              shouldAutoScrollRef.current = true;
              hasInitiallyScrolledRef.current = true;
            } else if (shouldAutoScrollRef.current && !isUserScrollingRef.current) {
              container.scrollTop = container.scrollHeight;
            }
          }
        }, 50);
      });
    } else if (messages.length === 0) {
      // Reset flag when messages are cleared
      hasInitiallyScrolledRef.current = false;
    }
  }, [messages]);

  const handleSubmit = async (message: string, files?: any[]) => {
    if (!chat) return;

    // Extract file IDs if they are objects with fileId property
    const fileIds = files?.map(f => f.fileId).filter(Boolean) as string[] || [];

    try {
      if (onCustomSubmit) {
        await onCustomSubmit(message, agentId, chat.collection_id, fileIds);
      } else {
        await postNewMessage({
          message,
          agent: agentId,
          chatId: chat.collection_id,
          files: fileIds,
        }).unwrap();
      }
    } catch (error) {
      console.error('Error posting message:', error);
    }
  };

  if (!chat) {
    return (
      <div className="h-full w-full flex items-center justify-center">
        <LoadingAnimation size="md" />
      </div>
    );
  }

  // Render chat panel
  const chatPanel = (
    <div className="flex-1 flex flex-col md:pt-0 pt-14 h-full relative">
      {/* Mobile header */}
      {mobileHeaderContent}

      {/* Desktop header */}
      {headerContent && (
        <header className="h-16 hidden md:flex items-center justify-between px-4 flex-shrink-0">
          {headerContent}
        </header>
      )}

      {/* Iframe controls (desktop only) */}
      {enableIframeManagement && iframeManager.state.showPanel && !iframeManager.state.isFullscreen && (
        <div className="hidden md:block">
          <IframeControls
            isExpanded={iframeManager.state.isExpanded}
            isFullscreen={iframeManager.state.isFullscreen}
            onToggleExpand={iframeManager.actions.toggleExpand}
            onClose={iframeManager.actions.closePanel}
          />
        </div>
      )}

      {/* Messages or Welcome */}
      {messages.length > 0 ? (
        <>
          <div ref={messagesContainerRef} className="flex-1 overflow-y-auto overflow-x-hidden">
            {enableIframeManagement ? (
              <>
                {/* Desktop: Messages without iframes */}
                <div className="hidden md:block">
                  <MessagesWithDateSeparators
                    messages={chatMessages}
                    messagesEndRef={messagesEndRef}
                    onDisplayIframe={
                      enableIframeManagement
                        ? (id: number) => iframeManager.actions.displayIframe(id)
                        : undefined
                    }
                    displayedIframeId={
                      enableIframeManagement && typeof iframeManager.state.displayedIframeId === 'number'
                        ? iframeManager.state.displayedIframeId
                        : null
                    }
                    style={{ maxWidth }}
                    useVoteMutation={useVoteMutation}
                    showAvatars={showAvatars}
                    compactMobileSpacing={compactMobileSpacing}
                  />
                </div>
                {/* Mobile: Messages with iframes inline */}
                <div className="block md:hidden">
                  <MessagesWithDateSeparators
                    messages={messages}
                    messagesEndRef={messagesEndRef}
                    useVoteMutation={useVoteMutation}
                    showAvatars={showAvatars}
                    compactMobileSpacing={compactMobileSpacing}
                  />
                </div>
              </>
            ) : (
              <MessagesWithDateSeparators
                messages={messages}
                messagesEndRef={messagesEndRef}
                style={{ maxWidth }}
                useVoteMutation={useVoteMutation}
                showAvatars={showAvatars}
                compactMobileSpacing={compactMobileSpacing}
              />
            )}
          </div>

          {/* Prompt bar */}
          <div
            className={`sticky bottom-0 bg-white bg-opacity-5 border-t border-gray-200 border-opacity-10 w-full flex-shrink-0 ${enableIframeManagement && iframeManager.state.isExpanded ? 'hidden' : ''
              }`}
          >
            <div className="p-2 md:p-4 w-full">
              <div className="mx-auto md:max-w-[48rem]">
                <UnifiedPromptBar
                  onSubmit={async ({ message, files }) => await handleSubmit(message, files)}
                  theme="widget"
                  features={{
                    fileUpload: true,
                    imagePaste: true,
                    dragAndDrop: true
                  }}
                />
              </div>
              <p className="text-center text-xs md:text-sm text-gray-500 mt-1 md:mt-2">
                AI can make mistakes, please make sure to check the answers provided.
              </p>
            </div>
          </div>
        </>
      ) : (
        <div className="flex-1 flex flex-col items-center justify-center px-2 md:px-4">
          <div className="w-full md:max-w-[48rem]">
            {welcomeMessage || <WelcomeMessage name="" />}
            {/* Prompt bar - Hide when iframe is expanded */}
            <div className={`mt-6 ${enableIframeManagement && iframeManager.state.isExpanded ? 'hidden' : ''}`}>
              <UnifiedPromptBar
                onSubmit={async ({ message, files }) => await handleSubmit(message, files)}
                theme="widget"
                features={{
                  fileUpload: true,
                  imagePaste: true,
                  dragAndDrop: true
                }}
              />
              <p className="text-center text-xs md:text-sm text-gray-500 mt-1 md:mt-2">
                AI can make mistakes, please make sure to check the answers provided.
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );

  // Use split-screen layout if iframe management enabled
  if (enableIframeManagement) {
    return (
      <SplitScreenLayout
        chatPanel={chatPanel}
        iframeManager={iframeManager}
      />
    );
  }

  return chatPanel;
}

